import React, { useCallback, useEffect, useState } from "react";
import { DocumentTextIcon, CheckCircleIcon, ClockIcon, PlusCircleIcon, EyeIcon, BanknotesIcon } from "@heroicons/react/24/outline";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import getAnnouncementsAnalytics from "../api/getAnnouncementsAnalytics";
import { Card, Kpi, KpiSkeleton, ErrorState, Empty, RefreshBtn, Segmented, useChartTheme, fmt } from "./monitoring/ui";

const PERIODS = [
  { value: "7d", label: "7 дней" },
  { value: "30d", label: "30 дней" },
  { value: "90d", label: "90 дней" },
  { value: "1y", label: "1 год" },
];

const GRANULARITY = [
  { value: "daily", label: "По дням" },
  { value: "weekly", label: "По неделям" },
  { value: "monthly", label: "По месяцам" },
];

const STATUS_META = {
  active: { label: "Активные", color: "bg-emerald-500" },
  pending: { label: "На модерации", color: "bg-amber-500" },
  rejected: { label: "Отклонённые", color: "bg-rose-500" },
  inactive: { label: "Неактивные", color: "bg-gray-400" },
};

const AnnouncementsAnalytics = () => {
  const [period, setPeriod] = useState("30d");
  const [granularity, setGranularity] = useState("daily");
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");
  const [error, setError] = useState(null);
  const chart = useChartTheme();

  const fetchAnalytics = useCallback(async () => {
    setState("loading");
    setError(null);
    try {
      setData(await getAnnouncementsAnalytics(period));
      setState("ready");
    } catch (err) {
      console.error("Error fetching announcements analytics:", err);
      setError(err.message);
      setState("error");
    }
  }, [period]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const overview = data?.overview || {};
  const summary = data?.summary || {};
  const byStatus = overview.by_status || {};
  const statusTotal = Object.values(byStatus).reduce((a, b) => a + (Number(b) || 0), 0);
  const series = (data?.trends?.[granularity] || []).map((i) => ({ label: i.date || i.week || i.month || "", count: i.count || 0 }));
  const categories = [...(data?.by_category || [])].map((c) => ({ name: c.name || "—", total: c.total || 0, active: c.active || 0 })).sort((a, b) => b.total - a.total);
  const catMax = Math.max(...categories.map((c) => c.total), 1);
  const forbidden = /доступ запрещ|403/i.test(error || "");

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-text-secondary">Публикации, модерация и категории объявлений.</p>
        <div className="flex items-center gap-2">
          <Segmented options={PERIODS} value={period} onChange={setPeriod} />
          <RefreshBtn onClick={fetchAnalytics} loading={state === "loading"} />
        </div>
      </div>

      {state === "error" ? (
        <ErrorState text={forbidden ? "Нет доступа к аналитике объявлений" : "Не удалось загрузить аналитику"} detail={error} onRetry={forbidden ? undefined : fetchAnalytics} />
      ) : !data ? (
        <>
          <KpiSkeleton />
          <div className="h-80 animate-pulse rounded-block border border-border-primary bg-bg-card" />
        </>
      ) : (
        <div className={`space-y-4 ${state === "loading" ? "opacity-60" : ""}`}>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <Kpi label="Всего объявлений" value={overview.total ?? 0} icon={DocumentTextIcon} />
            <Kpi label="Активных" value={byStatus.active ?? 0} icon={CheckCircleIcon} tone="bg-emerald-500/15 text-emerald-500" hint={overview.total ? `${((byStatus.active / overview.total) * 100).toFixed(0)}% от всех` : undefined} />
            <Kpi label="Ждут модерации" value={byStatus.pending ?? 0} icon={ClockIcon} tone="bg-amber-500/15 text-amber-500" />
            <Kpi label="Новых за неделю" value={overview.created_this_week ?? 0} icon={PlusCircleIcon} tone="bg-sky-500/15 text-sky-500" hint={`сегодня: ${fmt(overview.created_today ?? 0)}`} />
          </div>

          <Card title="Новые объявления" actions={<Segmented options={GRANULARITY} value={granularity} onChange={setGranularity} />}>
            {series.length ? (
              <div className="h-72 px-2 py-4">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={series} margin={{ top: 8, right: 16, left: -8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} vertical={false} />
                    <XAxis dataKey="label" tick={chart.axis} axisLine={false} tickLine={false} minTickGap={16} />
                    <YAxis tick={chart.axis} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip {...chart.tooltip} formatter={(v) => [fmt(v), "Объявлений"]} />
                    <Bar dataKey="count" fill="#14B8A6" radius={[4, 4, 0, 0]} maxBarSize={32} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <Empty>Нет новых объявлений за период</Empty>
            )}
          </Card>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <Card title="Статусы">
              {statusTotal ? (
                <div className="space-y-4 p-5">
                  <div className="flex h-3 overflow-hidden rounded-full bg-bg-tertiary">
                    {Object.entries(byStatus).map(([k, v]) => (
                      <div key={k} className={STATUS_META[k]?.color || "bg-gray-500"} style={{ width: `${(v / statusTotal) * 100}%` }} title={`${STATUS_META[k]?.label || k}: ${fmt(v)}`} />
                    ))}
                  </div>
                  <ul className="space-y-2 text-sm">
                    {Object.entries(byStatus)
                      .sort((a, b) => b[1] - a[1])
                      .map(([k, v]) => (
                        <li key={k} className="flex items-center justify-between gap-3">
                          <span className="flex items-center gap-2 text-text-secondary">
                            <span className={`h-2.5 w-2.5 rounded-full ${STATUS_META[k]?.color || "bg-gray-500"}`} />
                            {STATUS_META[k]?.label || k}
                          </span>
                          <span className="tabular-nums text-text-primary">
                            <b>{fmt(v)}</b>
                            <span className="ml-2 text-xs text-text-muted">{((v / statusTotal) * 100).toFixed(0)}%</span>
                          </span>
                        </li>
                      ))}
                  </ul>
                </div>
              ) : (
                <Empty />
              )}
            </Card>

            <Card title="Просмотры и цены" className="lg:col-span-2">
              <div className="grid grid-cols-1 divide-y divide-border-primary sm:grid-cols-3 sm:divide-x sm:divide-y-0">
                {[
                  { icon: EyeIcon, label: "Всего просмотров", value: fmt(summary.total_views ?? 0) },
                  { icon: EyeIcon, label: "В среднем на объявление", value: fmt(Math.round(summary.avg_views ?? 0)) },
                  { icon: BanknotesIcon, label: "Средняя цена", value: summary.avg_price ? `${fmt(Math.round(summary.avg_price))} ₸` : "—" },
                ].map(({ icon: Icon, label, value }) => (
                  <div key={label} className="p-5">
                    <p className="flex items-center gap-1.5 text-xs text-text-secondary">
                      <Icon className="h-4 w-4" /> {label}
                    </p>
                    <p className="mt-1 text-xl font-bold tabular-nums text-text-primary">{value}</p>
                  </div>
                ))}
              </div>
            </Card>
          </div>

          <Card title="Категории" subtitle="Всего и активных объявлений">
            {categories.length ? (
              <ul className="space-y-3 p-5">
                {categories.map((c) => (
                  <li key={c.name}>
                    <div className="mb-1 flex items-center justify-between gap-3 text-sm">
                      <span className="truncate text-text-primary">{c.name}</span>
                      <span className="shrink-0 tabular-nums text-text-secondary">
                        <b className="text-text-primary">{fmt(c.total)}</b>
                        <span className="ml-2 text-xs text-emerald-500">{fmt(c.active)} активных</span>
                      </span>
                    </div>
                    <div className="relative h-2 overflow-hidden rounded-full bg-bg-tertiary">
                      <div className="absolute inset-y-0 left-0 rounded-full bg-indigo-500/40" style={{ width: `${(c.total / catMax) * 100}%` }} />
                      <div className="absolute inset-y-0 left-0 rounded-full bg-emerald-500" style={{ width: `${(c.active / catMax) * 100}%` }} />
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <Empty />
            )}
          </Card>
        </div>
      )}
    </div>
  );
};

export default AnnouncementsAnalytics;
